import { openaiUsage } from "../openai/usage";
import { customAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

export const oracleOci = defineConnector({
  identifier: "oracleOci",
  name: "Oracle Cloud Infrastructure",
  tagLine: "Generative AI",
  logo,
  protocol: "http",
  tags: ["ai", "engineering"],
  defaultUrlPattern: () =>
    "https://inference\\.generativeai\\..*\\.oci\\.oraclecloud\\.com/.*",
  auth: (app) =>
    customAuth("oci", {
      tenancyOcid: app.config.tenancyOcid,
      userOcid: app.config.userOcid,
      fingerprint: app.config.fingerprint,
      compartmentId: app.config.compartmentId,
      privateKey: app.secret.privateKey,
    }),
  steps: [
    {
      title: "Create an OCI API signing key",
      items: [
        {
          kind: "markdown",
          content:
            "In the [OCI console](https://cloud.oracle.com), open **Profile > My profile > API keys** and add an API key. Download the private key (.pem) and copy the configuration preview, which lists the tenancy OCID, user OCID, and fingerprint.",
        },
      ],
    },
    {
      title: "Enter your OCI credentials",
      items: [
        {
          kind: "input",
          label: "Region",
          placeholder: "us-chicago-1",
          required: true,
          target: { kind: "configuration", field: "region" },
        },
        {
          kind: "input",
          label: "Tenancy OCID",
          placeholder: "ocid1.tenancy.oc1..…",
          required: true,
          target: { kind: "configuration", field: "tenancyOcid" },
        },
        {
          kind: "input",
          label: "User OCID",
          placeholder: "ocid1.user.oc1..…",
          required: true,
          target: { kind: "configuration", field: "userOcid" },
        },
        {
          kind: "input",
          label: "Fingerprint",
          placeholder: "aa:bb:cc:dd:…",
          required: true,
          target: { kind: "configuration", field: "fingerprint" },
        },
        {
          kind: "input",
          label: "Compartment OCID",
          placeholder: "ocid1.compartment.oc1..…",
          required: true,
          target: { kind: "configuration", field: "compartmentId" },
          helperText:
            "The compartment that holds your Generative AI access; sent as opc-compartment-id.",
        },
        {
          kind: "file",
          label: "Private key (.pem)",
          accept: ".pem",
          target: { kind: "secrets", field: "privateKey" },
        },
      ],
    },
  ],
  // A GET on the chat path returns 400 ("Content parsing error") once the
  // request is signed, but 404 ("Authorization failed") when the signature is
  // rejected — so a 400 confirms the credentials sign valid OCI requests.
  connectionTest: {
    protocol: "http",
    request: ({ region }) => ({
      url: `https://inference.generativeai.${region}.oci.oraclecloud.com/20231130/actions/v1/chat/completions`,
      expectedStatus: 400,
    }),
  },
  usage: openaiUsage(),
});
